// @ts-nocheck
import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";
import { toast } from "sonner";
import { Loader2, Plus, ExternalLink } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Link } from "react-router-dom";

interface Props {
  event: any;
  updateEvent: (patch: any) => void;
  eventId?: string;
}

export function CircuitSection({ event, updateEvent, eventId }: Props) {
  const queryClient = useQueryClient();
  const { organization } = useOrganization();
  const orgId = organization?.id;

  const { data: circuits = [], isLoading: loadingCircuits } = useQuery({
    queryKey: ["circuits-list", orgId],
    enabled: !!orgId,
    staleTime: 0,
    refetchOnMount: "always",
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("circuits")
        .select("id,name,circuit_type,age_group,gender,season,is_active")
        .eq("organization_id", orgId)
        .eq("is_active", true)
        .order("name");
      if (error) console.error("[CircuitSection] circuits query error:", error);
      console.log("[CircuitSection] orgId:", orgId, "circuits found:", data?.length, data);
      return data || [];
    },
  });

  const { data: scoringConfigs = [] } = useQuery({
    queryKey: ["scoring-configs", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("circuit_scoring_configs")
        .select("id,name,is_default")
        .eq("organization_id", orgId)
        .order("name");
      return data || [];
    },
  });

  const { data: links = [], refetch: refetchLinks } = useQuery({
    queryKey: ["circuit-event-links", eventId],
    enabled: !!eventId,
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("circuit_event_links")
        .select("*")
        .eq("event_id", eventId);
      return data || [];
    },
  });

  const [saving, setSaving] = useState(false);

  const toggleCircuit = async (circuitId: string, on: boolean) => {
    if (!eventId) {
      toast.error("Save the event first");
      return;
    }
    setSaving(true);
    if (on) {
      const defaultConfig = scoringConfigs.find((c: any) => c.is_default) || scoringConfigs[0];
      const { error } = await (supabase as any)
        .from("circuit_event_links")
        .insert({
          circuit_id: circuitId,
          event_id: eventId,
          scoring_config_id: defaultConfig?.id || null,
          is_live_period: false,
          point_multiplier: 1.0,
        });
      if (error) toast.error(error.message);
    } else {
      const { error } = await (supabase as any)
        .from("circuit_event_links")
        .delete()
        .eq("event_id", eventId)
        .eq("circuit_id", circuitId);
      if (error) toast.error(error.message);
    }
    await refetchLinks();
    setSaving(false);
  };

  const updateLink = async (linkId: string, patch: any) => {
    setSaving(true);
    const { error } = await (supabase as any)
      .from("circuit_event_links")
      .update(patch)
      .eq("id", linkId);
    if (error) toast.error(error.message);
    await refetchLinks();
    setSaving(false);
  };

  const enabled = !!event.circuit_event_enabled || links.length > 0;
  const answered = !!event.circuit_step_answered || links.length > 0;

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-bold tracking-tight">CIRCUIT</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Link this event to one or more circuits. Game results will award circuit ranking points.
        </p>
      </div>

      <div className="p-4 rounded-lg border border-border bg-card space-y-3">
        <div className="text-sm font-medium">Is this event part of a circuit?</div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() =>
              updateEvent({ circuit_event_enabled: true, circuit_step_answered: true })
            }
            className={
              "px-4 py-2 rounded-lg border text-sm font-medium transition-colors " +
              (answered && enabled
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border hover:bg-secondary")
            }
          >
            Yes
          </button>
          <button
            type="button"
            onClick={() =>
              updateEvent({ circuit_event_enabled: false, circuit_step_answered: true })
            }
            className={
              "px-4 py-2 rounded-lg border text-sm font-medium transition-colors " +
              (answered && !enabled
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border hover:bg-secondary")
            }
          >
            No
          </button>
        </div>
        {!answered && (
          <p className="text-xs text-muted-foreground">
            Choose Yes or No to mark this step complete.
          </p>
        )}
      </div>


      {enabled && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">Available circuits</h3>
            <Link
              to="/admin-dashboard/circuits"
              className="text-xs text-primary hover:underline flex items-center gap-1"
            >
              Manage circuits <ExternalLink className="w-3 h-3" />
            </Link>
          </div>

          {loadingCircuits ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : circuits.length === 0 ? (
            <div className="p-6 text-center border border-dashed border-border rounded-lg">
              <p className="text-sm text-muted-foreground mb-3">
                No circuits yet. Create one in the Circuits manager.
              </p>
              <Link
                to="/admin-dashboard/circuits"
                className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
              >
                <Plus className="w-4 h-4" /> Create a circuit
              </Link>
            </div>
          ) : (
            <div className="space-y-2">
              {circuits.map((c: any) => {
                const link = links.find((l: any) => l.circuit_id === c.id);
                const linked = !!link;
                return (
                  <div
                    key={c.id}
                    className="border border-border rounded-lg p-4 bg-card"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <label className="flex items-start gap-3 cursor-pointer flex-1">
                        <Checkbox
                          checked={linked}
                          disabled={saving || !eventId}
                          onCheckedChange={(v) => toggleCircuit(c.id, !!v)}
                        />
                        <div>
                          <div className="font-medium text-sm">{c.name}</div>
                          <div className="text-xs text-muted-foreground mt-0.5">
                            {[c.circuit_type, c.age_group, c.gender, c.season]
                              .filter(Boolean)
                              .join(" · ")}
                          </div>
                        </div>
                      </label>
                    </div>

                    {linked && (
                      <div className="mt-3 ml-7 grid sm:grid-cols-2 gap-3 pt-3 border-t border-border">
                        <div className="sm:col-span-2 text-xs text-muted-foreground">
                          Uses the circuit's point system. Adjust the multiplier
                          to weight this event (e.g. a championship).
                        </div>
                        <div>
                          <Label className="text-xs">Point Multiplier</Label>
                          <Input
                            type="number"
                            step="0.1"
                            min="0"
                            value={link.point_multiplier ?? 1}
                            onChange={(e) =>
                              updateLink(link.id, {
                                point_multiplier: Number(e.target.value),
                              })
                            }
                            className="h-9 mt-1"
                          />
                        </div>
                        <label className="flex items-center gap-2 text-sm">
                          <Checkbox
                            checked={!!link.is_live_period}
                            onCheckedChange={(v) =>
                              updateLink(link.id, { is_live_period: !!v })
                            }
                          />
                          Live period event
                        </label>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
